import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  Broadcast,
  CloudWatchMetricSeries,
  CreateBroadcast,
  CreateTestRun,
  MetricSample,
  PublisherCredentials,
  Recording,
  TestRunSummary,
  ViewerCredentials,
} from '@ivs-test/contracts';
import { getRuntimeConfig } from './runtime.js';

interface ListResponse<T> {
  items: T[];
}

export interface TestRun extends CreateTestRun {
  id: string;
  status: 'RUNNING' | 'ENDED';
  createdAt: string;
  updatedAt: string;
  endedAt?: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken.toString();
  const response = await fetch(`${getRuntimeConfig().apiUrl}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
    cache: 'no-store',
    credentials: 'include',
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({ message: response.statusText }))) as { message?: string };
    throw new Error(body.message ?? `API request failed: ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  listBroadcasts: () => request<ListResponse<Broadcast>>('/broadcasts'),
  getBroadcast: (id: string) => request<Broadcast>(`/broadcasts/${id}`),
  createBroadcast: (input: CreateBroadcast) =>
    request<Broadcast>('/broadcasts', { method: 'POST', body: JSON.stringify(input) }),
  deleteBroadcast: (id: string) => request<void>(`/broadcasts/${id}`, { method: 'DELETE' }),
  getIngest: (id: string) => request<PublisherCredentials>(`/broadcasts/${id}/ingest`),
  createPublisherSession: (id: string, clientId: string) =>
    request<PublisherCredentials>(`/broadcasts/${id}/publisher-sessions`, {
      method: 'POST',
      body: JSON.stringify({ clientId }),
    }),
  heartbeatPublisherSession: (sessionId: string, broadcastId: string) =>
    request<{ expiresAt: string }>(`/publisher-sessions/${sessionId}/heartbeat`, {
      method: 'POST',
      body: JSON.stringify({ broadcastId }),
    }),
  closePublisherSession: (sessionId: string, broadcastId: string) =>
    request<void>(`/publisher-sessions/${sessionId}?broadcastId=${encodeURIComponent(broadcastId)}`, {
      method: 'DELETE',
    }),
  getViewerCredentials: (id: string) =>
    request<ViewerCredentials>(`/broadcasts/${id}/viewer-token`, { method: 'POST' }),
  listRecordings: (broadcastId?: string) =>
    request<ListResponse<Recording>>(`/recordings${broadcastId ? `?broadcastId=${encodeURIComponent(broadcastId)}` : ''}`),
  getRecording: (id: string) => request<Recording>(`/recordings/${id}`),
  authorizeRecording: async (id: string) => {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken.toString();
    const response = await fetch(`/media-auth/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('録画再生の認可に失敗しました');
    return (await response.json()) as { mediaUrl: string; expiresAt: string };
  },
  listTestRuns: () => request<ListResponse<TestRun>>('/test-runs'),
  createTestRun: (input: CreateTestRun) =>
    request<TestRun>('/test-runs', { method: 'POST', body: JSON.stringify(input) }),
  endTestRun: (id: string) =>
    request<void>(`/test-runs/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'ENDED' }) }),
  postTelemetry: (id: string, samples: MetricSample[]) =>
    request<void>(`/test-runs/${id}/telemetry`, { method: 'POST', body: JSON.stringify({ samples }) }),
  getTelemetry: (id: string) => request<ListResponse<MetricSample>>(`/test-runs/${id}/telemetry`),
  getSummary: (id: string) => request<TestRunSummary>(`/test-runs/${id}/summary`),
  getCloudWatchMetrics: (id: string) =>
    request<ListResponse<CloudWatchMetricSeries>>(`/test-runs/${id}/cloudwatch`),
  getTime: () => request<{ epochMs: number; isoTime: string; region: string }>('/time'),
};
